import { useMutation, useQuery } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "../../../App";

export interface OnboardingStatus {
  completed: boolean;
  completed_at: string | null;
  /** DB ID of the ftux-onboarding persona (chat_config_id for dialogue creation) */
  persona_id: number | null;
}

const getOnboardingStatus = (): Promise<OnboardingStatus> => {
  return axios
    .get(`${nuclearn_api_url}/user/onboarding-status`)
    .then((response) => response.data);
};

/**
 * Fetch onboarding status directly (bypasses any react-query cache).
 * Use this right before creating an onboarding dialogue so we always have
 * the latest persona_id from the server, never a stale cached value.
 */
export const fetchOnboardingStatusFresh = getOnboardingStatus;

export const useGetOnboardingStatus = () => {
  return useQuery(["onboarding-status"], getOnboardingStatus, {
    // Always refetch on mount: onboarding state can change out-of-band
    // (admin reset, dev DB reset) and a stale cached `completed: true`
    // or missing `persona_id` would break the gate.
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
    retry: 1,
  });
};

const completeOnboarding = (): Promise<{ status: string }> => {
  return axios
    .post(`${nuclearn_api_url}/user/onboarding/complete`)
    .then((response) => response.data);
};

export const useCompleteOnboarding = () => {
  return useMutation(completeOnboarding);
};

/**
 * Atomically decide whether onboarding is needed AND create the FTUX dialogue
 * server-side. Returns {dialogue_id: number | null}. The frontend just
 * navigates to the returned dialogue — no client-side gate logic.
 */
export const initiateOnboarding = (): Promise<{
  dialogue_id: number | null;
}> => {
  return axios
    .post(`${nuclearn_api_url}/user/onboarding/initiate`)
    .then((response) => response.data);
};

/**
 * Fire a client-side onboarding telemetry event. The backend already fires
 * server-side events (onboarding_started, profile_validated, task_provided,
 * etc.); this endpoint covers the UI-only events that the server can't
 * observe directly — `verification_opened` and `citation_clicked`.
 *
 * Telemetry is best-effort — callers should swallow errors.
 */
export const reportOnboardingEvent = (
  eventName: "verification_opened" | "citation_clicked",
  metadata: Record<string, unknown> = {},
): Promise<void> => {
  return axios
    .post(`${nuclearn_api_url}/user/onboarding/event`, {
      event_name: eventName,
      metadata,
    })
    .then(() => undefined);
};
